// OPS-1 (2026-05-29) — reusable Task modal. One task object, surfaced from
// the header "+ Create → Task", the claim Tasks tab, and the Planner.
// Adjusters may assign tasks to peers on a shared claim (locked decision).
// CLAIM-OPTIONAL-TASKS (2026-06-05): a claim is now OPTIONAL. From a claim it
// defaults to that claim (locked); from anywhere else there's a picker that may
// be left blank to create a personal to-do (e.g. "renew notary") that still
// shows on the owner's task list.
import { useState, useEffect, useRef } from "react";
import { apiFetch, getUser } from "../api";

export default function TaskModal({
  claimId: fixedClaimId = null,
  claimLabel = null,
  task = null,            // when set → EDIT mode (pre-filled, PATCH on save, Delete shown)
  onClose,
  onSaved,
}) {
  const isEdit = !!task;
  const me = getUser();
  const [claim, setClaim] = useState(
    task ? (task.claim_id ? { id: task.claim_id, claim_number: task.claim_number } : null)
         : (fixedClaimId ? { id: fixedClaimId, claim_number: claimLabel } : null)
  );
  const [search, setSearch]   = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent]   = useState([]);
  const [users, setUsers]     = useState([]);
  const [form, setForm] = useState({
    title:       task?.title || "",
    due_date:    task?.due_date || "",
    priority:    task?.priority || "Medium",
    // Create: default the assignee to the current user (OPS-1, Kuhrt 2026-05-29).
    assigned_to: task ? (task.assigned_to ? String(task.assigned_to) : "") : (me?.id ? String(me.id) : ""),
    description: task?.description || "",
  });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const titleRef = useRef(null);

  // The claim picker is shown only on a global create with no claim chosen yet.
  const showPicker = !isEdit && !claim;

  useEffect(() => {
    apiFetch("/users/assignable").then(setUsers).catch(() => {});
    if (!fixedClaimId && !isEdit) apiFetch("/me/recent-claims").then(setRecent).catch(() => {});
  }, [fixedClaimId, isEdit]);

  useEffect(() => {
    if (fixedClaimId || !search.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/claims?search=${encodeURIComponent(search)}&limit=8`).then(d => setResults(d || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, fixedClaimId]);

  // Focus the title once a claim is locked in (the picker owns focus before that).
  useEffect(() => { if (!showPicker && titleRef.current) titleRef.current.focus(); }, [showPicker]);

  const save = async () => {
    setError("");
    if (!form.title.trim()) { setError("Enter a task title."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await apiFetch(`/tasks/${task.id}`, {
          method: "PATCH",
          body: {
            title: form.title.trim(),
            description: form.description || null,
            due_date: form.due_date || null,
            priority: form.priority,
            assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
          },
        });
      } else {
        // CLAIM-OPTIONAL-TASKS: unified create path; claim_id may be null.
        await apiFetch(`/tasks`, {
          method: "POST",
          body: {
            title: form.title.trim(),
            description: form.description || null,
            due_date: form.due_date || null,
            priority: form.priority,
            assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
            claim_id: claim ? claim.id : null,
          },
        });
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) { setError(e.message || "Save failed."); }
    setSaving(false);
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    setSaving(true);
    try {
      await apiFetch(`/tasks/${task.id}`, { method: "DELETE" });
      if (onSaved) onSaved();
      onClose();
    } catch (e) { setError(e.message || "Delete failed."); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header"><h3>{isEdit ? "Edit Task" : "New Task"}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {error && <div className="alert-error">{error}</div>}

          {/* Claim link — optional. Locked when launched from a claim; an optional
              picker otherwise; a quiet "Personal task" line when left blank. */}
          {claim ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#eef1f6", padding: "8px 12px", borderRadius: 4, marginBottom: 12 }}>
              <span><span style={{ fontSize: 11, color: "var(--text3)", marginRight: 8 }}>Claim</span><span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{claim.claim_number}</span></span>
              {!fixedClaimId && !isEdit && <button className="btn btn-outline" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => setClaim(null)}>Change</button>}
            </div>
          ) : showPicker ? (
            <div className="form-group full">
              <label>Claim <span style={{ color: "var(--text3)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional; leave blank for a personal task</span></label>
              <input autoFocus placeholder="Search claim # / insured / claimant…" value={search} onChange={e => setSearch(e.target.value)} />
              {(results.length > 0 || (!search && recent.length > 0)) && (
                <div style={pickList}>
                  {(search ? results : recent.slice(0, 8)).map(r => (
                    <div key={r.id} style={pickRow} onClick={() => { setClaim(r); setSearch(""); setResults([]); }}>
                      <span className="mono" style={{ color: "var(--blue)", fontWeight: 600 }}>{r.claim_number}</span>
                      <span style={{ color: "var(--text2)", marginLeft: 8, fontSize: 12 }}>{r.insured_name || r.claimant_name || ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // edit mode on a claimless task
            <div style={{ fontSize: 12, color: "var(--text3)", background: "#f7f7f9", padding: "6px 12px", borderRadius: 4, marginBottom: 12 }}>Personal task — not linked to a claim.</div>
          )}

          <div className="form-group full"><label>Task</label>
            <input ref={titleRef} placeholder="e.g. Request updated medical records — or 'renew notary commission'" value={form.title}
                   onChange={e => setForm({ ...form, title: e.target.value })}
                   onKeyDown={e => { if (e.key === "Enter") save(); }} /></div>
          <div className="form-grid">
            <div className="form-group"><label>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            <div className="form-group"><label>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
              </select></div>
          </div>
          <div className="form-group full"><label>Assignee</label>
            <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
              <option value="">Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select></div>
          <div className="form-group full"><label>Notes</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <div className="modal-footer" style={{ display: "flex", alignItems: "center" }}>
          {isEdit && <button className="btn btn-outline" disabled={saving} style={{ color: "#dc2626", borderColor: "#dc262644", marginRight: "auto" }} onClick={remove}>Delete</button>}
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Task")}</button>
        </div>
      </div>
    </div>
  );
}

const pickList = { border: "1px solid var(--border)", borderRadius: 4, marginTop: 4, maxHeight: 200, overflowY: "auto" };
const pickRow  = { padding: "7px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-light, #eee)" };
